import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Carousel from "../carousels/Carousel";
import { apiRequest } from "../../api/client";
import { useAuth } from "../../auth/AuthContext";
import { roomTitle } from "../Together/roomAppearance";
import { MemberAvatars, RoomCover, activeClock, featuredRoom, isUrgent, nextStep, useCountdown } from "../Together/roomUi";
import { useNotifications } from "../../notifications/NotificationContext";
import "./Home.css";
import SwipePromo from "./SwipePromo";

const backdrop = (path: string) => `https://image.tmdb.org/t/p/w1280${path}`;
const poster = (path: string) => `https://image.tmdb.org/t/p/w342${path}`;
const greeting = () => { const hour = new Date().getHours(); return hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening"; };

const HomeRoom = ({ room }: { room: any }) => {
  const step = nextStep(room);
  const clock = activeClock(room);
  const countdown = useCountdown(clock?.deadlineAt, room.serverTime, clock?.minutes);
  const timed = Boolean(clock) && countdown && !countdown.expired;
  return (
    <Link to={`/together?room=${room.sessionId}`} className={`continue-room continue-room--${step.tone}`}>
      <RoomCover room={room} className="continue-room__cover" />
      <span className="continue-room__text"><strong>{roomTitle(room)}</strong><small>{step.text}{timed ? ` · ${countdown!.label} left` : ""}</small><MemberAvatars members={room.members || []} showReady={["answering", "stalled"].includes(room.phase)} size={22} /></span>
      <b>{step.cta} →</b>
    </Link>
  );
};

type Feed = { featured: any; rails: { id: string; title: string; subtitle: string; items: any[] }[] };

const Home = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [feed, setFeed] = useState<Feed | null>(null);
  const [feedError, setFeedError] = useState(false);
  const [query, setQuery] = useState("");
  const { rooms: allRooms } = useNotifications();
  const [watchlist, setWatchlist] = useState<any[]>([]);

  useEffect(() => {
    apiRequest<Feed>("/movies/home").then(setFeed).catch(() => setFeedError(true));
  }, []);

  useEffect(() => {
    if (!user) { setWatchlist([]); return; }
    apiRequest<any>("/watchlist").then(data => setWatchlist((data.items || []).sort((a: any, b: any) => String(b.addedAt).localeCompare(String(a.addedAt))).slice(0, 12))).catch(() => undefined);
  }, [user]);

  const search = (event: FormEvent) => {
    event.preventDefault();
    const value = query.trim();
    navigate(value ? `/discover?q=${encodeURIComponent(value)}` : "/discover");
  };

  const featured = feed?.featured;
  // Rooms that need you come first; otherwise a few rooms to start a film night in.
  const urgent = allRooms.filter(isUrgent);
  // The live banner already features one room, so the list leaves it out.
  const bannerRoom = featuredRoom(allRooms);
  const rooms = (urgent.length ? urgent : allRooms.filter(room => room.phase !== "chosen" || room.filmNights)).filter(room => room.sessionId !== bannerRoom?.sessionId).slice(0, 4);

  return (
    <main className="home">
      <header className="hero">
        {featured?.backdropPath && <div className="hero__backdrop" style={{ backgroundImage: `url(${backdrop(featured.backdropPath)})` }} aria-hidden="true" />}
        <div className="hero__inner">
          <p className="hero__eyebrow">{user?.givenName ? `${greeting().toUpperCase()}, ${user.givenName.toUpperCase()}` : "YOUR NEXT GREAT WATCH STARTS HERE"}</p>
          <h1 className="hero__headline">Stop scrolling.<br /><span>Start watching.</span></h1>
          <p className="hero__intro">Search for a film, get picks that match your mood and streaming services, or decide together with friends.</p>
          <form className="hero__search" onSubmit={search} role="search">
            <span aria-hidden="true">⌕</span>
            <input value={query} onChange={event => setQuery(event.target.value)} placeholder="Search for a film…" aria-label="Search for a film" />
            <button>Search</button>
          </form>
          <div className="hero__actions">
            <Link to="/discover" className="hero-action hero-action--primary"><span aria-hidden="true">✦</span><div><strong>Get recommendations</strong><small>Your mood, your services</small></div></Link>
            <Link to={user ? "/together" : "/account"} className="hero-action"><span aria-hidden="true">◎</span><div><strong>Find a film together</strong><small>Answer privately, choose as one</small></div></Link>
          </div>
        </div>
      </header>

      {user && (rooms.length > 0 || watchlist.length > 0) && (
        <section className="home-section continue" aria-labelledby="continue-heading">
          <div className="home-section__heading"><p>{urgent.length > 1 ? "ALSO HAPPENING" : "PICK UP WHERE YOU LEFT OFF"}</p><h2 id="continue-heading">Your film nights</h2></div>
          {rooms.length > 0 && <div className="continue__rooms">{rooms.map(room => <HomeRoom key={room.sessionId} room={room} />)}</div>}
          {watchlist.length > 0 && <div className="continue__watchlist">
            <div className="continue__label"><span>From your watchlist</span><Link to="/history">See all →</Link></div>
            <div className="continue__posters">{watchlist.map(item => (
              <Link key={item.movieId} to={`/film/${item.movieId}`} className="continue-poster">
                {item.posterPath ? <img src={poster(item.posterPath)} alt={`${item.title} poster`} loading="lazy" /> : <span className="continue-poster__blank">{item.title}</span>}
              </Link>
            ))}</div>
          </div>}
        </section>
      )}

      {user && <SwipePromo posters={(feed?.rails[2]?.items || []).map((film: any) => film.posterPath).filter(Boolean)} />}

      {!user && (
        <section className="home-section how" aria-labelledby="how-heading">
          <div className="home-section__heading"><p>HOW IT WORKS</p><h2 id="how-heading">Film night, sorted in three steps</h2></div>
          <ol className="how__steps">
            <li><span>01</span><h3>Tell us the mood</h3><p>Pick genres, a minimum rating and the services you actually pay for.</p></li>
            <li><span>02</span><h3>See where it streams</h3><p>Every pick shows where you can stream, rent or buy it in the UK.</p></li>
            <li><span>03</span><h3>Decide together</h3><p>Friends answer privately, then you browse one shared shortlist live.</p></li>
          </ol>
          <Link to="/account" className="how__cta">Create a free account <span aria-hidden="true">→</span></Link>
        </section>
      )}

      {featured && (
        <section className="home-section">
          <Link to={`/film/${featured.id}`} className="spotlight">
            {featured.backdropPath && <img className="spotlight__image" src={backdrop(featured.backdropPath)} alt="" />}
            <div className="spotlight__body">
              <p>#1 TRENDING THIS WEEK</p>
              <h2>{featured.title}</h2>
              <div className="spotlight__meta"><span>★ {(Number(featured.voteAverage) / 2).toFixed(1)} / 5</span><span>{featured.releaseDate?.slice(0, 4)}</span></div>
              <span className="spotlight__overview">{featured.overview}</span>
              <span className="spotlight__cta">View film <span aria-hidden="true">→</span></span>
            </div>
          </Link>
        </section>
      )}

      {feed?.rails.map(rail => rail.items.length > 0 && <Carousel key={rail.id} films={rail.items} eyebrow={rail.subtitle} title={rail.title} highlight="" />)}
      {!feed && !feedError && <div className="home-loading" aria-live="polite">Loading what’s on…</div>}
      {feedError && <p className="home-loading">We couldn’t load today’s films. Please refresh to try again.</p>}
    </main>
  );
};

export default Home;
